/**
 * Subscription Plans Service - Multi-Tenant Business Model Engine
 *
 * This module manages the subscription plan system that powers the MDL Fan platform's
 * flexible monetisation model. Each channel/club can offer customised subscription
 * tiers tailored to their fanbase, from single match passes to full season packages.
 *
 * Key architectural decisions:
 * - Plans are channel-specific, not global (each club sets their own pricing)
 *   WHY: Clubs have different cost structures, fan demographics, and market positions
 * - Dynamic plan availability based on season, events, and promotions
 *   WHY: Sports have seasons - plans must adapt to fixture schedules
 * - React Query with 15-second refresh for near-real-time pricing updates
 *   WHY: Flash sales, limited offers, and dynamic pricing require fresh data
 *
 * Business model philosophy:
 * - Flexible monetisation: Clubs choose what works for their fans
 * - Tiered access: Casual fans (matchpass) to superfans (season pass)
 * - Bundle economics: Higher commitment = better value per match
 * - Promotional flexibility: Flash sales, early bird, loyalty discounts
 *
 * Plan types and their business rationale:
 * - MATCHPASS: Low commitment entry point for casual fans
 *   WHY: Reduces barrier to entry, captures impulse purchases
 * - SEASON PASS: High-value package for committed supporters
 *   WHY: Predictable revenue, increased fan engagement
 * - BUNDLES (5/10 matches): Middle ground for regular viewers
 *   WHY: Increases average transaction value whilst maintaining flexibility
 * - GIFT PACKAGES: Social sharing and gifting mechanism
 *   WHY: Organic growth through fan networks, seasonal revenue spikes
 *
 * Revenue optimisation strategies:
 * - Psychological pricing (£9.99 vs £10)
 * - Anchoring with strikethrough prices
 * - Urgency through limited-time offers
 * - Social proof via "popular" badges
 *
 * @module apiPlans
 * @see {@link src/features/plans/usePlans.js} - React Query integration with auto-refresh
 * @see {@link src/features/plans/Plans.jsx} - UI carousel for plan selection
 * @see {@link docs/09_PAYMENT_SUBSCRIPTION_SYSTEM.md} - Complete payment documentation
 */

import { getData } from './apiFetch';
import type { PlanResponse } from '../types/api';

/**
 * Retrieves available subscription plans for the current channel.
 *
 * This endpoint returns channel-specific pricing and plan configurations that
 * adapt based on multiple business factors:
 * - Current season/competition schedule
 * - Active promotional campaigns
 * - User's existing subscriptions (upgrade/downgrade paths)
 * - Geographic location (regional pricing)
 * - Time-based offers (early bird, last-minute deals)
 *
 * WHY dynamic plans instead of static configuration:
 * - Sports content value fluctuates (big match vs friendly)
 * - Promotional flexibility for marketing campaigns
 * - A/B testing different pricing strategies
 * - Seasonal adjustments (off-season discounts)
 *
 * Business intelligence in plan ordering:
 * - Plans sorted by 'priority' field (backend ML optimisation)
 * - Popular plans highlighted to leverage social proof
 * - Promotional plans positioned for maximum visibility
 * - Recurring subscriptions prioritised for predictable revenue
 *
 * Plan eligibility rules (enforced server-side):
 * - Can't purchase plan if already owned (shows "Purchased")
 * - Upgrade paths calculated based on prorated values
 * - Downgrade restrictions during active billing period
 * - Geographic restrictions for broadcast rights
 *
 * Performance considerations:
 * - 15-second cache in React Query (fresh pricing for flash sales)
 * - 5 retry attempts (critical for purchase flow)
 * - Lightweight payload (~2KB) for mobile performance
 *
 * @returns {Promise<PlanResponse[]>} Array of plan objects with pricing and metadata
 * @throws {Error} If API fails or channel has no configured plans
 *
 * @example
 * // Typical response for football club during season
 * [
 *   {
 *     planId: "season-2024",
 *     name: "2024 Season Pass",
 *     price: "79.99",
 *     stripePriceId: "price_xxx", // Stripe integration
 *     billing: "one-time",
 *     attributes: ["All 46 matches", "HD streaming", "Match replays"],
 *     isPopular: true,
 *     hasPurchased: false,
 *     promo: { originalPrice: "99.99", text: "Early bird - Save 20%" }
 *   },
 *   {
 *     planId: "matchpass",
 *     name: "Single Match Pass",
 *     price: "4.99",
 *     stripePriceId: "price_yyy",
 *     billing: "one-time",
 *     attributes: ["One match", "48-hour access", "HD streaming"],
 *     isPopular: false,
 *     hasPurchased: false
 *   }
 * ]
 */
export async function getPlans(): Promise<PlanResponse[]> {
  // WHY 'plans' endpoint instead of including in channel config:
  // - Plans change frequently (promotions, availability)
  // - User-specific data (hasPurchased) requires auth context
  // - Reduces initial channel config payload size
  // - Allows independent caching strategies (channel=long, plans=short)
  const path = 'plans';
  const data = await getData<PlanResponse[]>({ path });
  return data;
}
